<script setup lang="ts">
  import { inject, ref, watch } from 'vue';

  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { formatDateToPtBR } from '@/shared/lib';
  import { ColoredButton, DateRangePicker, IconImage, NumberStepperInput } from '@/shared/ui';

  import {
    createDocumentSelectionFilterContext,
    DocumentSelectionFilterInjectionKey,
    type DocumentSelectionFilters,
  } from '../../model/documentSelectionFilterContext';

  const props = withDefaults(
    defineProps<{
      currentFilters?: DocumentSelectionFilters;
    }>(),
    {
      currentFilters: () => ({}),
    }
  );

  const { isFilterPanelOpen } = inject(
    DocumentSelectionFilterInjectionKey,
    () => createDocumentSelectionFilterContext(),
    true
  );

  const emit = defineEmits<{
    (e: 'clear'): void;
    (e: 'apply', filters: DocumentSelectionFilters): void;
  }>();

  const hasTraining = ref<boolean | null>(null);
  const startDate = ref<Date | null>(null);
  const endDate = ref<Date | null>(null);
  const revision = ref<number | null>(0);
  const isDateOpen = ref(false);

  function resetDraft(): void {
    hasTraining.value = props.currentFilters.hasTraining ?? null;
    startDate.value = props.currentFilters.startDate ?? null;
    endDate.value = props.currentFilters.endDate ?? null;
    revision.value = props.currentFilters.revision ?? 0;
    isDateOpen.value = false;
  }

  watch(() => props.currentFilters, resetDraft, { immediate: true });

  watch(isFilterPanelOpen, (isOpen) => {
    if (!isOpen) {
      resetDraft();
    }
  });

  function toggleTraining(value: boolean): void {
    hasTraining.value = hasTraining.value === value ? null : value;
  }

  function handleClear(): void {
    hasTraining.value = null;
    startDate.value = null;
    endDate.value = null;
    revision.value = 0;
    isDateOpen.value = false;
    emit('clear');
  }

  function handleApply(): void {
    emit('apply', {
      hasTraining: hasTraining.value,
      startDate: startDate.value,
      endDate: endDate.value,
      revision: revision.value && revision.value > 0 ? revision.value : null,
    });
  }
</script>

<template>
  <div
    data-testid="document-selection-filter-panel"
    class="flex flex-col gap-6 px-6 pt-4 pb-5 text-left"
  >
    <div class="flex flex-wrap items-start gap-6">
      <div class="flex shrink-0 flex-col gap-1.5">
        <span class="text-sm font-medium text-gray-07">Treinamento</span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            data-testid="document-selection-filter-training-with"
            :class="[
              'rounded-lg px-3 py-2 text-sm font-medium',
              hasTraining === true ? 'bg-(--theme-secondary) text-white' : 'text-gray-07',
            ]"
            @click="toggleTraining(true)"
          >
            Com treinamento
          </button>
          <button
            type="button"
            data-testid="document-selection-filter-training-without"
            :class="[
              'rounded-lg px-3 py-2 text-sm font-medium',
              hasTraining === false ? 'bg-(--theme-secondary) text-white' : 'text-gray-07',
            ]"
            @click="toggleTraining(false)"
          >
            Sem treinamento
          </button>
        </div>
      </div>

      <div class="flex shrink-0 flex-col gap-1.5">
        <label
          for="document-selection-filter-date"
          class="text-sm font-medium text-gray-07"
        >
          Data ou período
        </label>
        <button
          id="document-selection-filter-date"
          type="button"
          data-testid="document-selection-filter-date"
          class="flex h-10.5 w-[204px] items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-4 text-sm font-medium text-gray-03 focus:border-(--theme-secondary-ink) focus:outline-none"
          @click="isDateOpen = true"
        >
          <IconImage
            :src="CalendarIcon"
            alt="Calendário"
            class="h-5 w-5 shrink-0"
          />
          <span class="truncate text-left">
            {{
              startDate
                ? `${formatDateToPtBR(startDate)}${endDate ? ` - ${formatDateToPtBR(endDate)}` : ''}`
                : 'Selecione uma data'
            }}
          </span>
        </button>
        <DateRangePicker
          v-model:start-date="startDate"
          v-model:end-date="endDate"
          v-model:is-open="isDateOpen"
          @apply="isDateOpen = false"
          @cancel="isDateOpen = false"
        />
      </div>

      <div class="flex shrink-0 flex-col gap-1.5">
        <label
          for="document-selection-filter-revision"
          class="text-sm font-medium text-gray-07"
        >
          Revisão
        </label>
        <NumberStepperInput
          input-id="document-selection-filter-revision"
          :model-value="revision"
          :min="0"
          :step="1"
          input-class="h-10.5 py-0"
          @update:model-value="revision = $event"
        />
      </div>
    </div>

    <div class="flex justify-end gap-4">
      <ColoredButton
        text="Limpar filtros"
        outlined
        color="gray-03"
        border-color="gray-03"
        data-testid="document-selection-filter-clear"
        @click="handleClear"
      />
      <ColoredButton
        text="Aplicar filtros"
        data-testid="document-selection-filter-apply"
        @click="handleApply"
      />
    </div>
  </div>
</template>
